import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { controlCenterApi, type AccountListItem, type AccountSearchParams } from '../../services/controlCenterApi'

export function AccountsList(): React.ReactElement {
  const [accounts, setAccounts] = useState<AccountListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<AccountSearchParams['status']>('')
  const [typeFilter, setTypeFilter] = useState<AccountSearchParams['account_type']>('')
  const [billingHoldFilter, setBillingHoldFilter] = useState(false)
  const [sortField, setSortField] = useState('name')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const fetchAccounts = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const params: AccountSearchParams = {
        q: debouncedQuery || undefined,
        status: statusFilter || undefined,
        account_type: typeFilter || undefined,
        billing_hold: billingHoldFilter ? 'true' : undefined,
        sort: sortField,
        direction: sortDirection,
        page,
        per_page: 25,
      }
      const data = await controlCenterApi.getAccounts(params)
      setAccounts(data.accounts)
      setTotalPages(data.meta.total_pages)
      setTotal(data.meta.total)
    } catch {
      setError('Failed to load accounts')
    }
    setIsLoading(false)
  }, [debouncedQuery, statusFilter, typeFilter, billingHoldFilter, sortField, sortDirection, page])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
    setPage(1)
  }

  const handleExport = async () => {
    const url = await controlCenterApi.exportAccountsCsvUrl({
      q: debouncedQuery || undefined,
      status: statusFilter || undefined,
      account_type: typeFilter || undefined,
      sort: sortField,
      direction: sortDirection,
    })
    window.open(url, '_blank')
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
      )
    }
    return (
      <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d={sortDirection === 'asc' ? 'M4.5 15.75l7.5-7.5 7.5 7.5' : 'M19.5 8.25l-7.5 7.5-7.5-7.5'} />
      </svg>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Accounts</h1>
          <p className="text-slate-400 text-sm mt-1">{total} accounts found</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export CSV
          </button>
          <Link
            to="/control_center/accounts/new"
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-slate-900 font-semibold text-sm rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Create Account
          </Link>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, domain, or ID..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none text-sm"
            />
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value as AccountSearchParams['status']); setPage(1) }}
            className="px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="deleted">Deleted</option>
          </select>

          {/* Type filter */}
          <select
            value={typeFilter}
            onChange={e => { setTypeFilter(e.target.value as AccountSearchParams['account_type']); setPage(1) }}
            className="px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
          >
            <option value="">All Types</option>
            <option value="rooftop">Rooftop</option>
            <option value="group">Group</option>
          </select>

          {/* Billing hold filter */}
          <label className="inline-flex items-center gap-2 px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors">
            <input
              type="checkbox"
              checked={billingHoldFilter}
              onChange={e => { setBillingHoldFilter(e.target.checked); setPage(1) }}
              className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-primary focus:ring-primary/50"
            />
            <span className="text-sm text-slate-300 whitespace-nowrap">Billing Hold</span>
          </label>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
            </svg>
            <span className="text-red-300 text-sm">{error}</span>
            <button type="button" onClick={fetchAccounts} className="ml-auto text-red-400 hover:text-red-300 text-sm underline">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left px-4 py-3 w-20">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">ID</span>
                </th>
                <th className="text-left px-4 py-3">
                  <button type="button" onClick={() => handleSort('name')} className="flex items-center gap-1 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors">
                    Name <SortIcon field="name" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 hidden md:table-cell">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Type</span>
                </th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Domain</span>
                </th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">
                  <button type="button" onClick={() => handleSort('active')} className="flex items-center gap-1 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors">
                    Status <SortIcon field="active" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 hidden xl:table-cell">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Users</span>
                </th>
                <th className="text-left px-4 py-3 hidden xl:table-cell">
                  <button type="button" onClick={() => handleSort('created_at')} className="flex items-center gap-1 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors">
                    Created <SortIcon field="created_at" />
                  </button>
                </th>
                <th className="text-right px-4 py-3">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <span className="text-slate-400 text-sm">Loading accounts...</span>
                    </div>
                  </td>
                </tr>
              ) : accounts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-10 h-10 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                      </svg>
                      <span className="text-slate-400 text-sm">No accounts found</span>
                      {debouncedQuery && (
                        <button type="button" onClick={() => setSearchQuery('')} className="text-primary hover:text-primary/80 text-sm">
                          Clear search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                accounts.map(account => {
                  const accountPath = `/control_center/accounts/${account.legacy_id ?? account.id}`
                  return (
                  <tr key={account.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-3">
                      <Link
                        to={accountPath}
                        className="text-sm font-mono text-slate-400 hover:text-primary transition-colors"
                      >
                        {account.legacy_id ?? '-'}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <Link
                          to={accountPath}
                          className="text-sm font-medium text-white hover:text-primary transition-colors"
                        >
                          {account.name}
                        </Link>
                        <p className="text-xs text-slate-500 mt-0.5">{account.email}</p>
                        {account.parent_name && (
                          <p className="text-xs text-slate-600 mt-0.5">↳ {account.parent_name}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        account.account_type === 'group'
                          ? 'bg-blue-500/15 text-blue-400'
                          : 'bg-slate-600/50 text-slate-300'
                      }`}>
                        {account.account_type === 'group' ? 'Group' : 'Rooftop'}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-sm text-slate-400">{account.domain || '-'}</span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <StatusBadge active={account.active} deleted={!!account.deleted_at} />
                        {account.is_on_billing_hold && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-400">
                            Hold
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      <span className="text-sm text-slate-400">{account.users_count}</span>
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      <span className="text-sm text-slate-400">
                        {new Date(account.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={accountPath}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-md transition-colors"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
            <p className="text-sm text-slate-400">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm text-slate-300 bg-slate-700 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-md transition-colors"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm text-slate-300 bg-slate-700 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-md transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function StatusBadge({ active, deleted }: { active: boolean; deleted: boolean }): React.ReactElement {
  if (deleted) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-500/15 text-red-400">
        <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
        Deleted
      </span>
    )
  }
  if (!active) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/15 text-yellow-400">
        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
        Inactive
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-500/15 text-green-400">
      <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
      Active
    </span>
  )
}
